'use client'

/**
 * The authority every action in this console is recorded under, and the one place that
 * knows which one it is.
 *
 * A presenter walking the FG6 ordered chain approves as Finance, then as the Program
 * Management Office, then as the Contracting Officer. Signing out and back in three times
 * is not a demo. The server already supports it: `acting_context` honours an
 * `X-Assumed-Authority` header, and does so only where the caller's own authority row is
 * unrestricted, so the mechanism is a real permission check rather than a costume.
 *
 * What the client did not have was anywhere to keep the choice. This provider holds it,
 * mirrors it into `sessionStorage` so refreshing mid-demo does not silently return the
 * presenter to their own authority, and publishes a nonce that mounted regions read in
 * their dependency arrays — a switch has to refetch every region, because every region's
 * answer depends on who is asking.
 *
 * It deliberately does **not** send the header itself. `lib/scc-api.ts` owns request
 * construction; this owns the state that request reads.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

/** The key `sessionStorage` holds the assumed authority under. Session-scoped on purpose:
 * closing the tab ends the impersonation, which is the conservative default for something
 * that changes what a write is attributed to. */
export const ASSUMED_AUTHORITY_STORAGE_KEY = 'scc.assumed_authority'

/** `acting_body` from the server: who is acting, and under what. */
export interface ActingBody {
  actor: string
  actor_role: string | null
  authority_code: string | null
  authority_title: string | null
  channel: string
  is_exercising_foreign_authority: boolean
  binds_government?: boolean
  is_unrestricted?: boolean
}

/** The identity that signed in, as distinct from the authority it is exercising. */
export interface IdentityHolder {
  authority_code: string
  authority_title: string
  is_unrestricted: boolean
  may_assume: boolean
  may_assume_reason: string
}

/**
 * One row of the switcher's menu.
 *
 * `is_illustrative` is not decoration: every seeded persona is fictional and says so on
 * its own row, per build spec §11.4 item 6.
 */
export interface PersonaOption {
  authority_code: string
  authority_title: string
  abbreviation: string | null
  role_names?: string[]
  binds_government: boolean
  can_override: boolean
  obligates_funds: boolean
  /** Derived server-side from the gate slot templates. Never a literal list in the client. */
  gates: string[]
  owns_stage_outputs?: string[]
  open_approval_count?: number | null
  seeded_identity?: {
    display_name: string
    email: string
    agency_code: string | null
    is_illustrative: boolean
  } | null
  is_current: boolean
  sort_order?: number
}

export interface IdentityPayload {
  acting: ActingBody
  holder: IdentityHolder
  assumable: PersonaOption[]
  as_of: string
  assumable_count: number
}

export interface ActingIdentityValue {
  /** The authority code the console is acting under, or `null` for the holder's own. */
  assumedAuthority: string | null
  /**
   * Bumped on every switch. Regions put it in their dependency array so that changing
   * persona refetches rather than leaving the previous persona's answers on screen.
   */
  nonce: number
  setAssumedAuthority: (code: string | null) => void
  /** Force every mounted region to refetch without changing persona. */
  refetchAll: () => void
}

const ActingIdentityContext = createContext<ActingIdentityValue | undefined>(
  undefined
)

export function ActingIdentityProvider({
  children,
}: {
  children: React.ReactNode
}) {
  // Read from storage after mount rather than in the initialiser: this component renders
  // on the server first, where `sessionStorage` does not exist, and seeding state from it
  // during render is a hydration mismatch.
  const [assumedAuthority, setAssumed] = useState<string | null>(null)
  const [nonce, setNonce] = useState(0)

  useEffect(() => {
    try {
      const stored = window.sessionStorage.getItem(
        ASSUMED_AUTHORITY_STORAGE_KEY
      )
      if (stored) setAssumed(stored)
    } catch {
      // Storage disabled or unavailable. The demo still works; it just forgets the
      // persona on refresh, which is a smaller failure than not rendering.
    }
  }, [])

  const setAssumedAuthority = useCallback((code: string | null) => {
    setAssumed(code)
    setNonce((n) => n + 1)
    try {
      if (code) {
        window.sessionStorage.setItem(ASSUMED_AUTHORITY_STORAGE_KEY, code)
      } else {
        window.sessionStorage.removeItem(ASSUMED_AUTHORITY_STORAGE_KEY)
      }
    } catch {
      // As above.
    }
  }, [])

  const refetchAll = useCallback(() => setNonce((n) => n + 1), [])

  const value = useMemo<ActingIdentityValue>(
    () => ({ assumedAuthority, nonce, setAssumedAuthority, refetchAll }),
    [assumedAuthority, nonce, setAssumedAuthority, refetchAll]
  )

  return (
    <ActingIdentityContext.Provider value={value}>
      {children}
    </ActingIdentityContext.Provider>
  )
}

/**
 * The acting identity, or a neutral value where the provider is not mounted.
 *
 * Returning a default rather than throwing keeps a component usable in isolation — a
 * region that reads the nonce should not be the reason a page fails to render.
 */
export function useActingIdentity(): ActingIdentityValue {
  const context = useContext(ActingIdentityContext)
  if (context) return context
  return {
    assumedAuthority: null,
    nonce: 0,
    setAssumedAuthority: () => undefined,
    refetchAll: () => undefined,
  }
}

/**
 * The assumed authority, read outside React.
 *
 * `lib/scc-api.ts` builds requests in plain async functions with no component around
 * them, so it cannot use the hook. This is the same value the context holds.
 */
export function readAssumedAuthority(): string | null {
  if (typeof window === 'undefined') return null
  try {
    return window.sessionStorage.getItem(ASSUMED_AUTHORITY_STORAGE_KEY)
  } catch {
    return null
  }
}
